import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { PostHogInstance } from 'shared/posthogCapture/types';

interface FeedbackProperties {
  feedback: string;
}

interface ChallengeVerificationProperties {
  step:
    | 'CHALLENGE_VERIFICATION_ANSWER'
    | 'CHALLENGE_DESCRIPTION_FEEDBACK'
    | 'CHALLENGE_VERIFICATION_CONFIRMATION_ANSWER';
}

interface ConvoPrepSubmittedProperties {
  step:
    | 'CONVERSATION_PREP_START'
    | 'CONVERSATION_PREP_WITH_WHO'
    | 'CONVERSATION_PREP_GOAL'
    | 'CONVERSATION_PREP_WORRIED_BY';
}

interface ResolutionSubmittedProperties {
  step:
    | 'RESOLUTION_TAKEN_ACTION_ANSWER'
    | 'RESOLUTION_IS_RESOLVED_ANSWER'
    | 'RESOLUTION_SUMMARY_INPUT'
    | 'RESOLUTION_CONFIDENCE_ANSWER'
    | 'OUTCOME_VERIFICATION_ANSWER';
}

interface UnblockerProperties {
  step: 'UNBLOCKER_ANSWER' | 'UNBLOCKER_FOLLOW_UP_ANSWER';
}

export const createChatCaptureEvents = (posthog: PostHogInstance) => ({
  chatbarMicrophoneClickedOff: () => {
    posthog?.capture('chat / chatbar / microphone / clicked off');
  },
  chatbarMicrophoneClickedOn: () => {
    posthog?.capture('chat / chatbar / microphone / clicked on');
  },
  chatbarSubmittedMessage: () => {
    posthog?.capture('chat / chatbar / submitted message');
  },
  deleted: () => {
    posthog?.capture('chat / deleted');
  },
  feedbackRatedHelpful: () => {
    posthog?.capture('chat / feedback / rated helpful');
  },
  feedbackRatedUnhelpful: (properties: FeedbackProperties) => {
    posthog?.capture('chat / feedback / rated unhelpful', properties);
  },
  messageBackAndForthSubmitted: () => {
    posthog?.capture('chat / message / back and forth / submitted');
  },
  messageChallengeVerification: (
    properties: ChallengeVerificationProperties
  ) => {
    posthog?.capture('chat / message / challenge verification', properties);
  },
  messageClickableSuggestionsClickedRow: () => {
    posthog?.capture('chat / message / clickable suggestions / clicked row');
  },
  messageClickableSuggestionsRetrieved: () => {
    posthog?.capture('chat / message / clickable suggestions / retrieved');
  },
  messageConvoPrepClickedRow: () => {
    posthog?.capture('chat / message / convo prep / clicked row');
  },
  messageConvoPrepRetreived: () => {
    posthog?.capture('chat / message / convo prep / retreived');
  },
  messageInitialPromptConvoPrepSubmitted: (
    properties: ConvoPrepSubmittedProperties
  ) => {
    posthog?.capture(
      'chat / message / initial prompt / convo prep / submitted',
      properties
    );
  },
  messageInitialPromptPromptHelperSubmittedGoal: () => {
    posthog?.capture(
      'chat / message / initial prompt / prompt helper / submitted goal'
    );
  },
  messageInitialPromptPromptHelperSubmittedTheme: () => {
    posthog?.capture(
      'chat / message / initial prompt / prompt helper / submitted theme'
    );
  },
  messageInitialPromptSelectedConvoPrep: () => {
    posthog?.capture('chat / message / initial prompt / selected convo prep');
  },
  messageInitialPromptSelectedPolicyHelper: () => {
    posthog?.capture(
      'chat / message / initial prompt / selected policy helper'
    );
  },
  messageInitialPromptSelectedPromptHelper: () => {
    posthog?.capture(
      'chat / message / initial prompt / selected prompt helper'
    );
  },
  messageInitialPromptSelectedUnionRules: () => {
    posthog?.capture('chat / message / initial prompt / selected union rules');
  },
  messageInitialPromptSelectedWarningLetter: () => {
    posthog?.capture(
      'chat / message / initial prompt / selected warning letter'
    );
  },
  messageInitialPromptSubmitted: () => {
    posthog?.capture('chat / message / initial prompt / submitted');
  },
  messageResolutionSubmitted: (properties: ResolutionSubmittedProperties) => {
    posthog?.capture('chat / message / resolution / submitted', properties);
  },
  messageUnblocker: (properties: UnblockerProperties) => {
    posthog?.capture('chat / message / unblocker', properties);
  },
  openedExistingChat: () => {
    posthog?.capture('chat / opened existing chat');
  },
  statusMarkedAsDone: () => {
    posthog?.capture('chat / status / marked as done');
  },
  statusRemovedAsGoal: () => {
    posthog?.capture('chat / status / removed as goal');
  },
  statusSelectedDiscussMore: () => {
    posthog?.capture('chat / status / selected discuss more');
  },
  tabsSelectedAllConversations: () => {
    posthog?.capture('chat / tabs / selected all conversations');
  },
  tabsSelectedDrafts: () => {
    posthog?.capture('chat / tabs / selected drafts');
  },
  tabsSelectedInProgress: () => {
    posthog?.capture('chat / tabs / selected in progress');
  },
  tabsSelectedResolved: () => {
    posthog?.capture('chat / tabs / selected resolved');
  },
});

export type ChatCaptureEvents = ReturnType<typeof createChatCaptureEvents>;

export const usePosthogCapture = () => {
  const posthog = usePostHog();
  return useMemo(() => createChatCaptureEvents(posthog), [posthog]);
};
